import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import type {
  AttachmentCategory,
  ChatSummaryDto,
  MessageSearchQuery,
  MessageSearchResultDto,
  PublicUser,
} from '@private-messenger/shared';

import { api } from '../api.js';

interface SearchPanelProps {
  chats: ChatSummaryDto[];
  users: PublicUser[];
  onClose: () => void;
  onOpen: (result: MessageSearchResultDto) => Promise<void>;
}

const attachmentTypes: Array<{ value: AttachmentCategory; label: string }> = [
  { value: 'image', label: 'Изображения' },
  { value: 'video', label: 'Видео' },
  { value: 'audio', label: 'Аудио' },
  { value: 'document', label: 'Документы' },
  { value: 'archive', label: 'Архивы' },
  { value: 'other', label: 'Другие файлы' },
];

export function SearchPanel({ chats, users, onClose, onOpen }: SearchPanelProps) {
  const [form, setForm] = useState({
    q: '',
    chatId: '',
    senderId: '',
    attachmentType: '',
    dateFrom: '',
    dateTo: '',
  });
  const [query, setQuery] = useState<MessageSearchQuery | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);
  const search = useQuery({
    queryKey: ['message-search', query],
    queryFn: () => api.searchMessages(query as MessageSearchQuery),
    enabled: Boolean(query),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setOpenError(null);
    const next: MessageSearchQuery = {
      limit: 50,
      ...(form.q.trim().length >= 2 ? { q: form.q.trim() } : {}),
      ...(form.chatId ? { chatId: form.chatId } : {}),
      ...(form.senderId ? { senderId: form.senderId } : {}),
      ...(form.attachmentType ? { attachmentType: form.attachmentType as AttachmentCategory } : {}),
      ...(form.dateFrom ? { dateFrom: new Date(`${form.dateFrom}T00:00:00`).toISOString() } : {}),
      ...(form.dateTo ? { dateTo: new Date(`${form.dateTo}T23:59:59.999`).toISOString() } : {}),
    };
    if (Object.keys(next).length > 1) setQuery(next);
  };

  return (
    <div className="search-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="message-search"
        role="dialog"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <h2>Поиск по перепискам</h2>
            <p>Текст, файлы, отправитель и период</p>
          </div>
          <button onClick={onClose} aria-label="Закрыть поиск">
            ×
          </button>
        </header>
        <form onSubmit={submit}>
          <label className="search-query-field">
            Запрос
            <input
              autoFocus
              value={form.q}
              placeholder="Текст, имя или название файла"
              onChange={(event) => setForm({ ...form, q: event.target.value })}
            />
          </label>
          <label>
            Чат
            <select
              value={form.chatId}
              onChange={(event) => setForm({ ...form, chatId: event.target.value })}
            >
              <option value="">Все чаты</option>
              {chats.map((chat) => (
                <option value={chat.id} key={chat.id}>
                  {chat.title}
                </option>
              ))}
            </select>
          </label>
          <label>
            Отправитель
            <select
              value={form.senderId}
              onChange={(event) => setForm({ ...form, senderId: event.target.value })}
            >
              <option value="">Любой</option>
              {users.map((user) => (
                <option value={user.id} key={user.id}>
                  {user.displayName}
                </option>
              ))}
            </select>
          </label>
          <label>
            Тип вложения
            <select
              value={form.attachmentType}
              onChange={(event) => setForm({ ...form, attachmentType: event.target.value })}
            >
              <option value="">Любой</option>
              {attachmentTypes.map((type) => (
                <option value={type.value} key={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            С даты
            <input
              type="date"
              value={form.dateFrom}
              onChange={(event) => setForm({ ...form, dateFrom: event.target.value })}
            />
          </label>
          <label>
            По дату
            <input
              type="date"
              value={form.dateTo}
              onChange={(event) => setForm({ ...form, dateTo: event.target.value })}
            />
          </label>
          <button className="primary-button" type="submit">
            Найти
          </button>
        </form>
        <div className="search-results">
          {search.isFetching ? <p className="empty-state">Ищем в доступных чатах…</p> : null}
          {search.error instanceof Error ? (
            <p className="form-error">{search.error.message}</p>
          ) : null}
          {openError ? <p className="form-error">{openError}</p> : null}
          {search.data && search.data.results.length === 0 ? (
            <p className="empty-state">Ничего не найдено.</p>
          ) : null}
          {search.data?.results.map((result) => (
            <button
              key={result.message.id}
              onClick={() => {
                setOpenError(null);
                void onOpen(result).catch((error: unknown) => {
                  setOpenError(
                    error instanceof Error ? error.message : 'Не удалось открыть сообщение',
                  );
                });
              }}
            >
              <span className="search-result-meta">
                <strong>{result.chat.title}</strong>
                <time>{new Date(result.message.createdAt).toLocaleString('ru-RU')}</time>
              </span>
              <b>{result.message.author?.displayName ?? 'Системное сообщение'}</b>
              <span>
                {result.message.text ||
                  result.message.attachments
                    .map((item) => item.caption || item.originalName)
                    .join(', ')}
              </span>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
